import { useTranslations } from '@vp/om-translations';
import { Button, Span, VisuallyHidden } from '@vp/swan';
import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { trackExpressCheckoutAddToCartClick } from '../../../apis/newRelicTrackingApi';
import { reorderForAltCheckout } from '../../../apis/oreosApi';
import { trackExpressCheckoutProductAddedToCart } from '../../../apis/trackingApi';
import {
  MY_ACCOUNT_PAGE_SECTION,
  MY_ACCOUNT_PAGE_STAGE,
  EXPRESS_CHECKOUT_ACTION,
  BUY_IT_AGAIN_EXPRESS_CHECKOUT_SOURCE,
  BUY_IT_AGAIN_PAGE_NAME,
  BUY_IT_AGAIN_SOURCE_PAGE,
  ORDER_HISTORY_SOURCE_PAGE,
  ORDER_HISTORY_PAGE_NAME,
  ORDER_HISTORY_EXPRESS_CHECKOUT_SOURCE,
  ORDER_DETAILS_SOURCE_PAGE,
  ORDER_DETAILS_PAGE_NAME,
  ORDER_DETAILS_EXPRESS_CHECKOUT_SOURCE,
} from '../../../constants/orderdetailsui';
import { LineItemReorderActionStatusContext } from '../../contexts/LineItemReorderActionStatusContext';
import useSiteData from '../../../hooks/useSiteData';
import { getShopperId } from '../../../util/auth';
import { BasicPreloader } from '../BasicPreloader';
import { useLineItemData } from '../../contexts/LineItemDataProvider';
import { LineItemActionsContext } from '../../contexts/LineItemActionsContext';
import { SourcePageContext } from '../../contexts/SourcePageContext';
import { DynamicExpressCheckout } from '../expressCheckout/DynamicExpressCheckout';

const pageTracking = {
  [BUY_IT_AGAIN_SOURCE_PAGE]: {
    page: BUY_IT_AGAIN_SOURCE_PAGE,
    pageName: BUY_IT_AGAIN_PAGE_NAME,
    source: BUY_IT_AGAIN_EXPRESS_CHECKOUT_SOURCE,
  },
  [ORDER_HISTORY_SOURCE_PAGE]: {
    page: ORDER_HISTORY_SOURCE_PAGE,
    pageName: ORDER_HISTORY_PAGE_NAME,
    source: ORDER_HISTORY_EXPRESS_CHECKOUT_SOURCE,
  },
  [ORDER_DETAILS_SOURCE_PAGE]: {
    page: ORDER_DETAILS_SOURCE_PAGE,
    pageName: ORDER_DETAILS_PAGE_NAME,
    source: ORDER_DETAILS_EXPRESS_CHECKOUT_SOURCE,
  },
  // tracking will not work correctly if this button is placed on a new page without updating this map
};

export const ItemBuyNowButton = ({
  quantityOverride,
  sourcePageOverride,
  ...buttonProps
}) => {
  const [orderDetailsMessages] = useTranslations(['orderDetailsMessages']);
  const item = useLineItemData();
  const { displayPosition } = useContext(LineItemActionsContext);
  const { sourcePage: defaultSourcePage } = useContext(SourcePageContext);
  const sourcePage = sourcePageOverride || defaultSourcePage;
  const tracking = pageTracking[sourcePage] || {};

  const { orderNumber, productName, id: lineItemId } = item;
  const quantity = quantityOverride || item.quantity;
  // fallback to productKey if no custom analytics product_id exists
  const productId =
    item.customOrderData?.analytics?.product_id || item.productKey;

  const shopperId = getShopperId();

  const { tenantEnvironment, vistacartTenant: vistacartTenantName } =
    useSiteData();

  const buyNowBtnRef = useRef(null);

  const [expressCheckoutIsLoading, setExpressCheckoutIsLoading] =
    useState(false);

  const { setAddToCartFailure, resetReorderActionFeedback } = useContext(
    LineItemReorderActionStatusContext
  );

  const [showExpressCheckoutFlyout, setShowExpressCheckoutFlyout] =
    useState(false);
  // used to ensure we don't attempt to set focus on cta before the cta is actually interacted with (e.g. on page load).
  const [ctaHasBeenClicked, setCtaHasBeenClicked] = useState(false);
  const [cartDetails, setCartDetails] = useState(null);

  const { page, source, pageName } = tracking;

  const onClickBuyNowHandler = async () => {
    resetReorderActionFeedback(EXPRESS_CHECKOUT_ACTION);

    setExpressCheckoutIsLoading(true);
    setCtaHasBeenClicked(true);

    trackExpressCheckoutAddToCartClick(shopperId, {
      itemId: lineItemId,
      vistacartTenantName,
      page,
    });

    const reorderItems = [
      {
        lineItemId,
        quantity,
        analytics: {
          source,
        },
        displayPosition,
        productId,
      },
    ];

    // prep alt cart for checkout
    try {
      const { altCartId } = await reorderForAltCheckout({
        tenantEnvironment,
        orderNumber,
        reorderItems,
        vistacartTenantName,
        reorderSourcePage: page,
        reorderSourcePageName: pageName,
      });

      setCartDetails({
        type: 'alt',
        altCartId,
      });

      setShowExpressCheckoutFlyout(true);

      trackExpressCheckoutProductAddedToCart({
        lineItemId,
        displayName: productName,
        productId,
        pageName,
      });
    } catch (e) {
      // show alert box to user
      setAddToCartFailure(true);
    } finally {
      // take the preloader out of the button
      setExpressCheckoutIsLoading(false);
    }
  };

  useEffect(() => {
    if (
      !showExpressCheckoutFlyout &&
      ctaHasBeenClicked &&
      buyNowBtnRef.current
    ) {
      buyNowBtnRef.current.focus();
    }
  }, [showExpressCheckoutFlyout, ctaHasBeenClicked]);

  return (
    <>
      <Button
        onClick={onClickBuyNowHandler}
        ref={buyNowBtnRef}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...buttonProps}
      >
        {expressCheckoutIsLoading && <BasicPreloader />}
        {/* This padding prevents the button from resizing when the preloader appears/disappears */}
        <Span mx={expressCheckoutIsLoading ? 0 : 3}>
          {orderDetailsMessages.buyNowButtonText}
        </Span>
        <VisuallyHidden>{`: ${productName}`}</VisuallyHidden>
      </Button>
      {showExpressCheckoutFlyout && (
        <DynamicExpressCheckout
          cartDetails={cartDetails}
          showExpressCheckoutFlyout={showExpressCheckoutFlyout}
          setShowExpressCheckoutFlyout={setShowExpressCheckoutFlyout}
          pageDetails={{
            pageName,
            pageSection: MY_ACCOUNT_PAGE_SECTION,
            pageStage: MY_ACCOUNT_PAGE_STAGE,
          }}
        />
      )}
    </>
  );
};
ItemBuyNowButton.propTypes = {
  quantityOverride: PropTypes.number,
  sourcePageOverride: PropTypes.string,
  ...Button.propTypes,
};
